import { useEffect, useRef, useCallback } from 'react'
import { useSystemStore, SystemStats } from '@/stores/system'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import {
  Cpu,
  MemoryStick,
  MessageSquare,
  Clock,
  Zap,
  Thermometer,
  Activity,
  AlertTriangle,
  Loader2,
  WifiOff,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Users,
} from 'lucide-react'
import { cn, formatBytes, formatDuration, formatNumber } from '@/lib/utils'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'

const MAX_HISTORY = 60

interface HistoryPoint {
  time: string
  gpuUtil0?: number
  gpuUtil1?: number
  gpuVram0?: number
  gpuVram1?: number
  cpuUsage: number
  memoryUsage: number
}

function buildHistoryPoint(stats: SystemStats): HistoryPoint {
  const point: HistoryPoint = {
    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    cpuUsage: stats.cpuUsage,
    memoryUsage: stats.memoryUsage,
  }
  stats.gpus.forEach((gpu) => {
    if (gpu.id === 0) {
      point.gpuUtil0 = gpu.utilization
      point.gpuVram0 = gpu.memoryTotal > 0 ? Math.round((gpu.memoryUsed / gpu.memoryTotal) * 100) : 0
    } else if (gpu.id === 1) {
      point.gpuUtil1 = gpu.utilization
      point.gpuVram1 = gpu.memoryTotal > 0 ? Math.round((gpu.memoryUsed / gpu.memoryTotal) * 100) : 0
    }
  })
  return point
}

export default function DashboardPage() {
  const { stats, fetchStats, error, isLoading, lastUpdated, queueStats, routerStats, fetchQueueStats, fetchRouterStats } = useSystemStore()
  const historyRef = useRef<HistoryPoint[]>([])

  const appendHistory = useCallback((s: SystemStats) => {
    const point = buildHistoryPoint(s)
    historyRef.current = [...historyRef.current.slice(-(MAX_HISTORY - 1)), point]
  }, [])

  useEffect(() => {
    fetchStats()
    fetchQueueStats()
    fetchRouterStats()
    const interval = setInterval(() => {
      fetchStats()
      fetchQueueStats()
      fetchRouterStats()
    }, 5000)
    return () => clearInterval(interval)
  }, [fetchStats, fetchQueueStats, fetchRouterStats])

  useEffect(() => {
    if (stats) appendHistory(stats)
  }, [stats, appendHistory])

  const staleSeconds = lastUpdated ? Math.floor((Date.now() - lastUpdated) / 1000) : null
  const history = historyRef.current

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            System overview and real-time metrics
          </p>
        </div>
        {isLoading && !stats && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Connecting...</span>
          </div>
        )}
        {stats && lastUpdated && (
          <div className="text-xs text-muted-foreground">
            Last updated: {new Date(lastUpdated).toLocaleTimeString()}
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
          <WifiOff className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-500">Cannot reach gateway</p>
            <p className="text-xs text-muted-foreground">{error}</p>
            <p className="text-xs text-muted-foreground">
              Make sure the gateway is running and{' '}
              <code className="bg-muted px-1 rounded">VITE_GATEWAY_URL</code> is correct.
              {staleSeconds !== null && staleSeconds > 10 && (
                <> Data is {staleSeconds}s stale.</>
              )}
            </p>
          </div>
        </div>
      )}

      {!stats && !isLoading && !error && (
        <div className="flex items-center gap-3 p-4 rounded-lg border border-border bg-muted/30">
          <AlertTriangle className="h-5 w-5 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No data available yet.</p>
        </div>
      )}

      {!stats && !error && isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {stats && (
        <>
          {/* Quick Health Banner */}
          <div className="flex flex-wrap gap-3 p-3 rounded-lg border border-border bg-card/60">
            {[
              { label: 'Gateway', ok: true },
              { label: 'vLLM', ok: stats.llms.length > 0 && stats.llms.some(l => l.status === 'online') },
              ...stats.gpus.map(g => ({
                label: `GPU ${g.id}`,
                ok: g.memoryUsed > 100,
                detail: `${Math.round((g.memoryUsed / g.memoryTotal) * 100)}% · ${g.temperature}°C`,
              })),
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-1.5 text-sm">
                <span className={cn(
                  'h-2 w-2 rounded-full shrink-0',
                  s.ok ? 'bg-emerald-500 shadow-[0_0_6px_hsl(142_71%_45%/0.5)]' : 'bg-red-500'
                )} />
                <span className="font-medium">{s.label}</span>
                {'detail' in s && s.detail && <span className="text-xs text-muted-foreground">{s.detail}</span>}
              </div>
            ))}
            {stats.llms.length > 0 && (
              <div className="flex items-center gap-1.5 text-sm ml-auto">
                <Zap className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs text-muted-foreground">{stats.llms[0].model}</span>
              </div>
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Active Sessions</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.activeSessions}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatNumber(stats.totalMessages)} total messages
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">System Uptime</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatDuration(stats.uptime)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Since last restart
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">CPU Usage</CardTitle>
                <Cpu className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.cpuUsage}%</div>
                <Progress value={stats.cpuUsage} className="mt-2" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Memory Usage</CardTitle>
                <MemoryStick className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.memoryUsage}%</div>
                <Progress value={stats.memoryUsage} className="mt-2" />
                {stats.memoryUsedMB && stats.memoryTotalMB && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatBytes(stats.memoryUsedMB * 1024 * 1024)} / {formatBytes(stats.memoryTotalMB * 1024 * 1024)}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* GPU Status */}
          {stats.gpus.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {stats.gpus.map((gpu) => (
                <Card key={gpu.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Zap className="h-5 w-5 text-primary" />
                      <span>GPU {gpu.id} - {gpu.name}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2 text-sm">
                          <Thermometer className="h-4 w-4 text-amber-500/90" />
                          <span className="text-muted-foreground">Temperature</span>
                        </div>
                        <p className={`text-2xl font-bold ${gpu.temperature > 80 ? 'text-red-500' : gpu.temperature > 65 ? 'text-amber-500' : ''}`}>
                          {gpu.temperature}°C
                        </p>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2 text-sm">
                          <Activity className="h-4 w-4 text-sky-400/90" />
                          <span className="text-muted-foreground">Utilization</span>
                        </div>
                        <p className="text-2xl font-bold">{gpu.utilization}%</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">VRAM Usage</span>
                        <span>
                          {formatBytes(gpu.memoryUsed * 1024 * 1024)} /{' '}
                          {formatBytes(gpu.memoryTotal * 1024 * 1024)}
                        </span>
                      </div>
                      <Progress
                        value={gpu.memoryTotal > 0 ? (gpu.memoryUsed / gpu.memoryTotal) * 100 : 0}
                        className="h-2"
                      />
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Power Draw</span>
                      <span>
                        {Math.round(gpu.powerDraw)}W
                        {gpu.powerLimit ? ` / ${Math.round(gpu.powerLimit)}W` : ''}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="flex items-center gap-3 py-8">
                <AlertTriangle className="h-5 w-5 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  No GPUs detected. Make sure NVIDIA drivers are installed and nvidia-smi is available.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Real-Time Charts */}
          {history.length >= 2 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* GPU Utilization Chart */}
              {stats.gpus.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">GPU Utilization</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={180}>
                      <AreaChart data={history} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="time" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} interval="preserveStartEnd" />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(v) => `${v}%`} />
                        <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                        <Area type="monotone" dataKey="gpuUtil0" name="GPU 0" stroke="hsl(217 91% 60%)" fill="hsl(217 91% 60% / 0.15)" strokeWidth={1.5} dot={false} />
                        {history.some((p) => p.gpuUtil1 !== undefined) && (
                          <Area type="monotone" dataKey="gpuUtil1" name="GPU 1" stroke="hsl(142 71% 45%)" fill="hsl(142 71% 45% / 0.15)" strokeWidth={1.5} dot={false} />
                        )}
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* GPU VRAM Chart */}
              {stats.gpus.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">VRAM Usage</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={180}>
                      <AreaChart data={history} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="time" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} interval="preserveStartEnd" />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(v) => `${v}%`} />
                        <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                        <Area type="monotone" dataKey="gpuVram0" name="GPU 0 VRAM" stroke="hsl(280 67% 55%)" fill="hsl(280 67% 55% / 0.15)" strokeWidth={1.5} dot={false} />
                        {history.some((p) => p.gpuVram1 !== undefined) && (
                          <Area type="monotone" dataKey="gpuVram1" name="GPU 1 VRAM" stroke="hsl(25 95% 53%)" fill="hsl(25 95% 53% / 0.15)" strokeWidth={1.5} dot={false} />
                        )}
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* CPU + Memory Chart */}
              <Card className={stats.gpus.length === 0 ? 'lg:col-span-2' : ''}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">CPU & Memory</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={180}>
                    <AreaChart data={history} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="time" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} interval="preserveStartEnd" />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(v) => `${v}%`} />
                      <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                      <Area type="monotone" dataKey="cpuUsage" name="CPU" stroke="hsl(47 96% 53%)" fill="hsl(47 96% 53% / 0.15)" strokeWidth={1.5} dot={false} />
                      <Area type="monotone" dataKey="memoryUsage" name="Memory" stroke="hsl(346 77% 50%)" fill="hsl(346 77% 50% / 0.15)" strokeWidth={1.5} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}

          {/* LLM Status */}
          <Card>
            <CardHeader>
              <CardTitle>LLM Services</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.llms.length > 0 ? (
                <div className="space-y-4">
                  {stats.llms.map((llm) => (
                    <div
                      key={llm.port}
                      className="flex items-center justify-between p-4 border border-border rounded-lg bg-muted/30"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <div
                            className={`h-2 w-2 rounded-full shrink-0 ${
                              llm.status === 'online'
                                ? 'bg-emerald-500 shadow-[0_0_6px_hsl(142_71%_45%/0.5)]'
                                : llm.status === 'loading'
                                ? 'bg-amber-400 animate-pulse'
                                : 'bg-rose-500'
                            }`}
                          />
                          <span className="font-medium">{llm.model}</span>
                          <span className="text-sm text-muted-foreground">
                            :{llm.port}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {formatNumber(llm.tokensGenerated)} tokens generated
                        </div>
                      </div>
                      <div className="text-right space-y-1">
                        <div className="text-sm">
                          {llm.requestsPerMinute} req/min
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {llm.averageLatency}ms avg
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4">
                  No LLM services configured. Set LLM_SERVICE_URL_1 / LLM_BASE_URL on the gateway.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Queue & Router Stats */}
          {(queueStats || routerStats) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {queueStats && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Queue Stats
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: 'Waiting', value: queueStats.waiting, color: 'text-amber-400' },
                        { label: 'Active', value: queueStats.active, color: 'text-blue-400' },
                        { label: 'Completed', value: queueStats.completed, color: 'text-emerald-400' },
                        { label: 'Failed', value: queueStats.failed, color: 'text-red-400' },
                        { label: 'Delayed', value: queueStats.delayed, color: 'text-purple-400' },
                        { label: 'Paused', value: queueStats.paused, color: 'text-zinc-400' },
                      ].map((item) => (
                        <div key={item.label} className="text-center space-y-0.5">
                          <p className={`text-xl font-bold ${item.color}`}>{item.value}</p>
                          <p className="text-xs text-muted-foreground">{item.label}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              {routerStats && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      Router Stats
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center space-y-0.5">
                        <p className="text-xl font-bold text-blue-400">{routerStats.messagesReceived}</p>
                        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                          <ArrowDownRight className="h-3 w-3" /> Received
                        </p>
                      </div>
                      <div className="text-center space-y-0.5">
                        <p className="text-xl font-bold text-emerald-400">{routerStats.messagesRouted}</p>
                        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                          <ArrowUpRight className="h-3 w-3" /> Routed
                        </p>
                      </div>
                      <div className="text-center space-y-0.5">
                        <p className="text-xl font-bold text-red-400">{routerStats.messagesFailed}</p>
                        <p className="text-xs text-muted-foreground">Failed</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-4 pt-2 border-t border-border text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {routerStats.clients} clients</span>
                      <span>{routerStats.agents} agents</span>
                      <span>{routerStats.queues} queues</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
